import {
  Clock,
  AlertCircle,
  Calendar,
  User,
  UserX,
  Search,
} from "lucide-react";
import { EServiceCategory, EServiceType } from "../interfaces/common";
import { SPLOOT_BADGE_COLOR } from "../themes";

/**
 * Session status enum
 */
export enum ESessionStatus {
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  RESCHEDULED = "RESCHEDULED",
  NO_SHOW = "NO_SHOW",
}

export enum ESessionFilterBy {
  UNASSIGNED = "UNASSIGNED",
}

export enum ESessionDetailsTabId {
  SESSION_DETAILS = "session-details",
  PARTNER_CUSTOMER_DETAILS = "partner-customer-details",
  TIMELINE = "timeline",
}

export enum AssignmentSource {
  AUTO_MATCHING = "AUTO_MATCHING",
  MANUAL = "MANUAL",
}

export enum ESessionType {
  ONE_TIME = "ONE_TIME",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export enum ESessionActionView {
  DEFAULT = "DEFAULT",
  CANCEL = "CANCEL",
  RESCHEDULE = "RESCHEDULE",
  COMPLETE = "COMPLETE",
  ASSIGN = "ASSIGN",
  CHANGE = "CHANGE",
  MODIFY = "MODIFY",
  EDIT_INCLUSIONS = "EDIT_INCLUSIONS",
}

export enum EAssignPartnerMode {
  ASSIGN = "ASSIGN",
  CHANGE = "CHANGE",
}

// Enums for session actions
export enum AdminSessionRescheduleReasonEnum {
  // Customer reasons
  CUSTOMER_DOG_NOT_AVAILABLE = "CUSTOMER_DOG_NOT_AVAILABLE",
  CUSTOMER_PERSONAL_COMMITMENT = "CUSTOMER_PERSONAL_COMMITMENT",
  CUSTOMER_DATE_TIMING_ISSUE = "CUSTOMER_DATE_TIMING_ISSUE",
  CUSTOMER_BAD_WEATHER = "CUSTOMER_BAD_WEATHER",
  CUSTOMER_OTHER = "CUSTOMER_OTHER",

  // Partner reasons
  PARTNER_EMERGENCY = "PARTNER_EMERGENCY",
  PARTNER_HEALTH_ISSUE = "PARTNER_HEALTH_ISSUE",
  PARTNER_BAD_WEATHER = "PARTNER_BAD_WEATHER",
  PARTNER_TRANSPORT_PROBLEM = "PARTNER_TRANSPORT_PROBLEM",
  PARTNER_PERSONAL_LEAVE = "PARTNER_PERSONAL_LEAVE",
  PARTNER_OTHER = "PARTNER_OTHER",

  // Admin reasons
  ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE = "ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE",
  ADMIN_CUSTOMER_NOT_REACHABLE = "ADMIN_CUSTOMER_NOT_REACHABLE",
  ADMIN_PARTNER_REQUEST_FOR_RESCHEDULE = "ADMIN_PARTNER_REQUEST_FOR_RESCHEDULE",
  ADMIN_PARTNER_NOT_REACHABLE = "ADMIN_PARTNER_NOT_REACHABLE",
  ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS = "ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS",
  ADMIN_SUPPLY_ISSUE = "ADMIN_SUPPLY_ISSUE",
  ADMIN_OTHER = "ADMIN_OTHER",
}

export enum SessionCancellationReasonEnum {
  // Customer reasons
  CUSTOMER_DOG_NOT_AVAILABLE = "CUSTOMER_DOG_NOT_AVAILABLE",
  CUSTOMER_PERSONAL_COMMITMENT = "CUSTOMER_PERSONAL_COMMITMENT",
  CUSTOMER_DATE_TIMING_ISSUE = "CUSTOMER_DATE_TIMING_ISSUE",
  CUSTOMER_BAD_WEATHER = "CUSTOMER_BAD_WEATHER",
  CUSTOMER_OTHER = "CUSTOMER_OTHER",

  // Partner reasons
  PARTNER_EMERGENCY = "PARTNER_EMERGENCY",
  PARTNER_HEALTH_ISSUE = "PARTNER_HEALTH_ISSUE",
  PARTNER_BAD_WEATHER = "PARTNER_BAD_WEATHER",
  PARTNER_CUSTOMER_ASKED_TO_CANCEL = "PARTNER_CUSTOMER_ASKED_TO_CANCEL",
  PARTNER_TRANSPORT_PROBLEM = "PARTNER_TRANSPORT_PROBLEM",
  PARTNER_PERSONAL_LEAVE = "PARTNER_PERSONAL_LEAVE",
  PARTNER_OTHER = "PARTNER_OTHER",

  // Admin reasons
  ADMIN_WALKER_REQUEST_FOR_CANCELLATION = "ADMIN_WALKER_REQUEST_FOR_CANCELLATION",
  ADMIN_WALKER_NOT_REACHABLE = "ADMIN_WALKER_NOT_REACHABLE",
  ADMIN_USER_REQUEST_FOR_CANCELLATION = "ADMIN_USER_REQUEST_FOR_CANCELLATION",
  ADMIN_CUSTOMER_NOT_REACHABLE = "ADMIN_CUSTOMER_NOT_REACHABLE",
  ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS = "ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS",
  ADMIN_SUPPLY_ISSUE = "ADMIN_SUPPLY_ISSUE",
  ADMIN_SUBSCRIPTION_CANCELLED_BY_CUSTOMER = "ADMIN_SUBSCRIPTION_CANCELLED_BY_CUSTOMER",
  ADMIN_OTHER = "ADMIN_OTHER",
}

// Admin-only enums for frontend display (values keep ADMIN_ for backend compatibility)
export enum AdminRescheduleReasonEnum {
  CUSTOMER_REQUEST_FOR_RESCHEDULE = "ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE",
  CUSTOMER_NOT_REACHABLE = "ADMIN_CUSTOMER_NOT_REACHABLE",
  PARTNER_REQUEST_FOR_RESCHEDULE = "ADMIN_PARTNER_REQUEST_FOR_RESCHEDULE",
  PARTNER_NOT_REACHABLE = "ADMIN_PARTNER_NOT_REACHABLE",
  UNFAVOURABLE_WEATHER_CONDITIONS = "ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS",
  SUPPLY_ISSUE = "ADMIN_SUPPLY_ISSUE",
  OTHER = "ADMIN_OTHER",
}

export enum AdminCancellationReasonEnum {
  WALKER_REQUEST_FOR_CANCELLATION = "ADMIN_WALKER_REQUEST_FOR_CANCELLATION",
  WALKER_NOT_REACHABLE = "ADMIN_WALKER_NOT_REACHABLE",
  CUSTOMER_REQUEST_FOR_CANCELLATION = "ADMIN_USER_REQUEST_FOR_CANCELLATION",
  CUSTOMER_NOT_REACHABLE = "ADMIN_CUSTOMER_NOT_REACHABLE",
  UNFAVOURABLE_WEATHER_CONDITIONS = "ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS",
  SUPPLY_ISSUE = "ADMIN_SUPPLY_ISSUE",
  SUBSCRIPTION_CANCELLED_BY_CUSTOMER = "ADMIN_SUBSCRIPTION_CANCELLED_BY_CUSTOMER",
  OTHER = "ADMIN_OTHER",
}

export enum ECancelledByType {
  CUSTOMER = "Customer",
  PARTNER = "Partner",
  ADMIN = "Admin",
}

/**
 * Enum for who performed a session action (assigned/rescheduled)
 */
export enum ESessionActionActor {
  CUSTOMER = "Customer",
  SYSTEM = "System",
  ADMIN = "Admin",
  PARTNER = "Partner",
}

export enum ESessionImageVerificationStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum EImageViewerMode {
  VIEW_ONLY = "view-only",
  APPROVAL = "approval",
}

export enum ESessionDocumentType {
  SELFIE = "SELFIE",
  TSHIRT = "TSHIRT",
  SHOES = "SHOES",
  KIT = "KIT",
  BEFORE = "BEFORE",
  AFTER = "AFTER",
  AFTER_SESSION_CLEANUP = "AFTER_SESSION_CLEANUP",
  CANCELLATION_PROOF = "CANCELLATION_PROOF",
}

// Timeline Constants
export const SESSION_TIMELINE_EVENT_TYPES = {
  CREATED: "CREATED",
  PARTNER_ASSIGNED: "PARTNER_ASSIGNED",
  PARTNER_CHANGED: "PARTNER_CHANGED",
  RESCHEDULED: "RESCHEDULED",
  CANCELLED: "CANCELLED",
  UNATTENDED: "UNATTENDED",
  FINDING_PARTNER: "FINDING_PARTNER",
} as const;

export const SESSION_TIMELINE_EVENT_CONFIG = {
  [SESSION_TIMELINE_EVENT_TYPES.CREATED]: {
    icon: Calendar,
    iconColor: "text-blue-600",
    borderColor: "border-blue-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.PARTNER_ASSIGNED]: {
    icon: User,
    iconColor: "text-green-600",
    borderColor: "border-green-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.PARTNER_CHANGED]: {
    icon: UserX,
    iconColor: "text-orange-600",
    borderColor: "border-orange-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.RESCHEDULED]: {
    icon: Clock,
    iconColor: "text-yellow-600",
    borderColor: "border-yellow-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.CANCELLED]: {
    icon: AlertCircle,
    iconColor: "text-red-600",
    borderColor: "border-red-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.UNATTENDED]: {
    icon: AlertCircle,
    iconColor: "text-red-500",
    borderColor: "border-red-200",
  },
  [SESSION_TIMELINE_EVENT_TYPES.FINDING_PARTNER]: {
    icon: Search,
    iconColor: "text-purple-600",
    borderColor: "border-purple-200",
  },
} as const;

export const DEFAULT_TIMELINE_EVENT_CONFIG = {
  icon: AlertCircle,
  iconColor: "text-neutral-500",
  borderColor: "border-neutral-200",
};

export const SESSION_DETAILS_TAB_IDS: ESessionDetailsTabId[] = [
  ESessionDetailsTabId.SESSION_DETAILS,
  ESessionDetailsTabId.PARTNER_CUSTOMER_DETAILS,
  ESessionDetailsTabId.TIMELINE,
];

export const DEFAULT_SESSION_TAB = ESessionDetailsTabId.SESSION_DETAILS;

// Session Filter Constants
export const PARTNER_NAME_PLACEHOLDER = "Search partner name...";
export const PARTNER_PHONE_PLACEHOLDER = "Enter partner number";
export const CUSTOMER_PHONE_PLACEHOLDER = "Enter customer number";

// API-related constants using proper enums
export const SESSION_API_STATUS_OPTIONS: Record<ESessionStatus, string> = {
  [ESessionStatus.SCHEDULED]: "Scheduled",
  [ESessionStatus.COMPLETED]: "Completed",
  [ESessionStatus.CANCELLED]: "Cancelled",
  [ESessionStatus.RESCHEDULED]: "Rescheduled",
  [ESessionStatus.NO_SHOW]: "Unattended",
};

export const SESSION_API_SERVICE_TYPE_OPTIONS = [
  { value: EServiceType.SUBSCRIPTION, label: "Subscription" },
  { value: EServiceType.SINGLE, label: "Single" },
] as const;

export const SESSION_API_SERVICE_CATEGORY_OPTIONS = [
  { value: EServiceCategory.WALKING, label: "Walking" },
  { value: EServiceCategory.GROOMING, label: "Grooming" },
  { value: EServiceCategory.TRAINING, label: "Training" },
] as const;

export const SESSION_API_TYPE_OPTIONS = [
  { value: ESessionType.ONE_TIME, label: "One Time" },
  { value: ESessionType.SUBSCRIPTION, label: "Subscription" },
] as const;

export const SESSION_API_DEFAULTS = {
  PAGE: 1,
  LIMIT: 10,
  SORT_BY: "scheduled",
  SORT_ORDER: "desc",
} as const;

export const SESSION_SEARCH_PLACEHOLDER = "Search sessions...";
export const SESSION_ADD_BUTTON_TEXT = "Add Session";

// Session Summary Constants
export const SESSION_SUMMARY_CONSTANTS = {
  TITLE: "Session Summary",
  LATE_SESSION_LABEL: "Session Started Late",
  LATE_SESSION_DESCRIPTION: "minutes after the scheduled time.",
  LABELS: {
    RESCHEDULED_AT: "Rescheduled At",
    SESSION_DATE_TIME: "Session Date & Time",
    SESSION_START_TIME: "Session Start Time",
    SESSION_END_TIME: "Session End Time",
    TOTAL_DURATION: "Total Duration",
    DISTANCE: "Distance",
    OTP: "OTP",
    BEFORE: "Before",
    AFTER: "After",
    KIT: "Kit",
    MAP: "Map",
    CUSTOMER_FEEDBACK: "Customer Feedback",
  },
  STATUS_TEXT: {
    NOT_STARTED: "Not started",
    NOT_ENDED: "Not ended",
    VERIFICATION_NOT_NEEDED: "Verification not needed",
    NOT_PROVIDED: "Not provided",
    UNKNOWN_USER: "Unknown User",
  },
  ALT_TEXT: {
    BEFORE_SESSION: "Before session",
    AFTER_SESSION: "After session",
    KIT_IMAGE: "Kit image",
    ROUTE_MAP: "Route map",
  },
} as const;

export const SESSION_DRAWER_CONSTANTS = {
  LOADING: {
    FINDING: "Finding session details...",
    LOADING: "Loading session details...",
  },
  ERROR: {
    LOAD_FAILED: "Error loading session details. Please try again.",
  },
} as const;

// Update Status Constants
export const UPDATE_STATUS_CONSTANTS = {
  TITLE: "Update Status",
  SUBTITLE:
    "This session was not attended by the partner. Please update status and provide a reason.",
  BUTTONS: {
    MARK_AS_CANCELLED: "Mark As Cancelled",
    RESCHEDULE_SESSION: "Reschedule Session",
    MANUALLY_COMPLETE_SESSION: "Manually Complete Session",
    OPEN_TASK: "Open Task",
    CANCEL: "Cancel",
    CONFIRM_CANCELLATION: "Confirm Cancellation",
    CONFIRM_RESCHEDULE: "Confirm Reschedule",
    CONFIRM_COMPLETE: "Confirm Complete",
    DISCARD: "Discard",
    MARK_COMPLETE: "Mark Complete",
    MODIFY_SESSION: "Modify Session",
  },
  LOADING_TEXT: {
    CANCELLING: "Cancelling...",
    RESCHEDULING: "Rescheduling...",
    COMPLETING: "Completing...",
    PROCESSING: "Processing...",
  },
  FORMS: {
    CANCEL_SESSION: "Cancel Session",
    RESCHEDULE_SESSION: "Reschedule Session",
    COMPLETE_SESSION: "Complete Session",
    ASSIGN_PARTNER: "Assign Partner",
    CHANGE_PARTNER: "Change Partner",
    REASON_LABEL: "Reason*",
    DATE_LABEL: "Date*",
    TIME_LABEL: "Time*",
    ADDITIONAL_COMMENTS_LABEL: "Additional Comments",
    REASON_PLACEHOLDER: "Enter cancellation reason...",
    RESCHEDULE_REASON_PLACEHOLDER: "Enter reschedule reason...",
    COMPLETION_REASON_PLACEHOLDER: "Enter completion reason...",
    REASON_HELP_TEXT: "Provide details about why this action is being taken",
  },
  TASKS: {
    UPDATE_STATUS: "Update status for unattended session",
    CANCEL_WALK: "Cancel unattended walk",
    RESCHEDULE_WALK: "Reschedule unattended walk",
  },
  ERRORS: {
    CANCEL_FAILED: "Failed to cancel session",
    RESCHEDULE_FAILED: "Failed to reschedule session",
    COMPLETE_FAILED: "Failed to complete session",
  },
} as const;

export const SESSION_STATUS_BADGE_COLORS = {
  [ESessionStatus.SCHEDULED]: SPLOOT_BADGE_COLOR.NEUTRAL,
  [ESessionStatus.COMPLETED]: SPLOOT_BADGE_COLOR.GREEN,
  [ESessionStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
  [ESessionStatus.RESCHEDULED]: SPLOOT_BADGE_COLOR.BLUE,
  [ESessionStatus.NO_SHOW]: SPLOOT_BADGE_COLOR.RED,
} as const;

export const SESSION_SERVICE_CATEGORY_BADGE_COLORS = {
  [EServiceCategory.WALKING]: SPLOOT_BADGE_COLOR.BLUE,
  [EServiceCategory.GROOMING]: SPLOOT_BADGE_COLOR.PURPLE,
  [EServiceCategory.TRAINING]: SPLOOT_BADGE_COLOR.GREEN,
  [EServiceCategory.PRODUCTS]: SPLOOT_BADGE_COLOR.ORANGE,
} as const;

/**
 * Badge colors for service type
 * Used for displaying service type badges consistently across the application
 */
export const SESSION_SERVICE_TYPE_BADGE_COLORS = {
  [EServiceType.SUBSCRIPTION]: SPLOOT_BADGE_COLOR.ORANGE,
  [EServiceType.SINGLE]: SPLOOT_BADGE_COLOR.LIGHT_GRAY,
} as const;

export enum ModifySessionInitiatedByType {
  CUSTOMER = "CUSTOMER",
  PARTNER = "PARTNER",
  ADMIN = "ADMIN",
}

// Modify Session Form Enums
export enum ModifySessionStep {
  INITIATED_BY = "initiated-by",
  REASON_ACTION = "reason-action",
  CONFIRM = "confirm",
}

export enum ModifySessionReason {
  ADMIN_PARTNER_REQUEST_FOR_CANCELLATION = "ADMIN_PARTNER_REQUEST_FOR_CANCELLATION",
  ADMIN_PARTNER_NOT_REACHABLE = "ADMIN_PARTNER_NOT_REACHABLE",
  ADMIN_CUSTOMER_REQUEST_FOR_CANCELLATION = "ADMIN_CUSTOMER_REQUEST_FOR_CANCELLATION",
  ADMIN_CUSTOMER_NOT_REACHABLE = "ADMIN_CUSTOMER_NOT_REACHABLE",
  ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE = "ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE",
  ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS = "ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS",
  ADMIN_SUPPLY_ISSUE = "ADMIN_SUPPLY_ISSUE",
  ADMIN_SUBSCRIPTION_CANCELLED_BY_CUSTOMER = "ADMIN_SUBSCRIPTION_CANCELLED_BY_CUSTOMER",
  ADMIN_PARTNER_REQUEST_TO_COMPLETE_SESSION = "ADMIN_PARTNER_REQUEST_TO_COMPLETE_SESSION",
  ADMIN_OTHER = "ADMIN_OTHER",
  ADMIN_PARTNER_EMERGENCY = "ADMIN_PARTNER_EMERGENCY",
  ADMIN_PARTNER_BAD_WEATHER = "ADMIN_PARTNER_BAD_WEATHER",
  ADMIN_PARTNER_REQUEST_FOR_RESCHEDULE = "ADMIN_PARTNER_REQUEST_FOR_RESCHEDULE",
  ADMIN_PARTNER_ASKED_TO_UNASSIGN = "ADMIN_PARTNER_ASKED_TO_UNASSIGN",
}

export enum SessionChangeAction {
  CANCEL = "CANCEL",
  RESCHEDULE = "RESCHEDULE",
  COMPLETE = "COMPLETE",
  NO_CHANGE = "NO_CHANGE",
  NOT_ALLOWED = "NOT_ALLOWED",
}

export enum RequestSource {
  SYSTEM = "SYSTEM",
  CUSTOMER_APP = "CUSTOMER_APP",
  PARTNER_APP = "PARTNER_APP",
  WEB = "WEB",
  DASHBOARD = "DASHBOARD",
  SHOPIFY = "SHOPIFY",
}

export enum ESessionDetailsSectionVariant {
  DEFAULT = "default",
  SUCCESS = "success",
}

export enum EditRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
}

export enum EditTimelineRequestStatus {
  REQUESTED = "REQUESTED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
}

export enum InclusionStatus {
  DEFAULT = "default",
  NEW = "new",
  UPDATED = "updated",
  REMOVED = "removed",
}

export const INCLUSION_CONTAINER_STYLES: Record<InclusionStatus, string> = {
  [InclusionStatus.DEFAULT]: "bg-white",
  [InclusionStatus.NEW]: "bg-green-50 border-green-200",
  [InclusionStatus.UPDATED]: "bg-orange-50 border-orange-200",
  [InclusionStatus.REMOVED]: "bg-neutral-100 opacity-60",
};

/** Revised order total must stay at or above this (in rupees) when editing inclusions. */
export const EDIT_INCLUSIONS_MIN_ORDER_TOTAL = 800;

export const EDIT_INCLUSIONS_MIN_ORDER_TOTAL_MESSAGE = `Order total must be at least ₹${EDIT_INCLUSIONS_MIN_ORDER_TOTAL}.`;

export const INCLUSION_BADGE_STYLES: Record<
  Exclude<InclusionStatus, InclusionStatus.DEFAULT>,
  string
> = {
  [InclusionStatus.NEW]: "bg-green-500 text-white",
  [InclusionStatus.UPDATED]: "bg-orange-500 text-white",
  [InclusionStatus.REMOVED]: "bg-neutral-400 text-white",
};

export enum SessionTiming {
  PAST = "PAST",
  LESS_THAN_3_HOURS = "LESS_THAN_3_HOURS",
  GREATER_THAN_3_HOURS = "GREATER_THAN_3_HOURS",
  LESS_THAN_24_HOURS = "LESS_THAN_24_HOURS",
  GREATER_THAN_24_HOURS = "GREATER_THAN_24_HOURS",
}

/** New partner implication status (per cancellation/penalty/leave) */
export enum PartnerImplicationStatus {
  APPROVED = "APPROVED",
  CALCULATED = "CALCULATED",
  NOT_APPLICABLE = "NOT_APPLICABLE",
}

export enum PartnerLeaveImplication {
  NONE = "NONE",
  LEAVE_ADDED = "LEAVE_ADDED",
}

/**
 * Whether cancelling this session warrants refunding the customer, and who decides.
 *
 * Resolved server-side for a CANCEL action only — every other action still delivers the
 * service, so nothing is owed back.
 */
export enum SessionRefundImplication {
  /** No refund warranted. The control is hidden entirely. */
  NOT_APPLICABLE = "NOT_APPLICABLE",
  /** A refund is warranted but the admin decides. Rendered as an opt-in checkbox. */
  ELIGIBLE = "ELIGIBLE",
  /** A refund is issued unconditionally; the admin gets no say. Rendered as a notice. */
  AUTOMATIC = "AUTOMATIC",
}

/** How much of the session's value is refundable. */
export enum SessionRefundBasis {
  /** The session's full pro-rata share of the subscription payment. */
  FULL = "FULL",
}

/** New session partner implication (dashboard rules) */
export interface SessionPartnerImplication {
  cancellationAdjustmentImplication: PartnerImplicationStatus;
  penaltyImplication: PartnerImplicationStatus;
  leaveImplication: PartnerLeaveImplication;
}

export interface SessionChangeRuleV2 {
  action: SessionChangeAction;
  partnerImplication: SessionPartnerImplication;
}

// Modify Session Form Constants
export interface IReasonOption {
  value: ModifySessionReason;
  label: string;
}

export const ADMIN_REASON_OPTIONS: IReasonOption[] = [
  { value: ModifySessionReason.ADMIN_SUPPLY_ISSUE, label: "Supply Issue" },
  { value: ModifySessionReason.ADMIN_OTHER, label: "Other" },
];

export const PARTNER_REASON_OPTIONS: IReasonOption[] = [
  {
    value: ModifySessionReason.ADMIN_PARTNER_REQUEST_TO_COMPLETE_SESSION,
    label: "Partner Request to Complete Session",
  },
  {
    value: ModifySessionReason.ADMIN_PARTNER_ASKED_TO_UNASSIGN,
    label: "Partner Asked to Unassign"
  },
  {
    value: ModifySessionReason.ADMIN_PARTNER_NOT_REACHABLE,
    label: "Partner Not Reachable",
  },
  {
    value: ModifySessionReason.ADMIN_PARTNER_EMERGENCY,
    label: "Partner Emergency",
  },
  {
    value: ModifySessionReason.ADMIN_PARTNER_BAD_WEATHER,
    label: "Bad Weather",
  },
];

export const CUSTOMER_REASON_OPTIONS: IReasonOption[] = [
  {
    value: ModifySessionReason.ADMIN_CUSTOMER_REQUEST_FOR_CANCELLATION,
    label: "Customer Request for Cancel",
  },
  {
    value: ModifySessionReason.ADMIN_CUSTOMER_REQUEST_FOR_RESCHEDULE,
    label: "Customer Request for Reschedule",
  },
  {
    value: ModifySessionReason.ADMIN_CUSTOMER_NOT_REACHABLE,
    label: "Customer Not Reachable",
  },
  {
    value: ModifySessionReason.ADMIN_UNFAVOURABLE_WEATHER_CONDITIONS,
    label: "Bad Weather",
  },
];

// Modify Session Form Steps
export interface IModifySessionStep {
  id: ModifySessionStep;
  label: string;
}

export const MODIFY_SESSION_STEPS: IModifySessionStep[] = [
  { id: ModifySessionStep.INITIATED_BY, label: "Initiated By" },
  { id: ModifySessionStep.REASON_ACTION, label: "Reason & Action" },
  { id: ModifySessionStep.CONFIRM, label: "Confirm" },
];

// Modify Session Initiated By Options
export interface IInitiatedByOption {
  value: ModifySessionInitiatedByType;
  label: string;
}

export const INITIATED_BY_OPTIONS: IInitiatedByOption[] = [
  { value: ModifySessionInitiatedByType.CUSTOMER, label: "Customer" },
  { value: ModifySessionInitiatedByType.PARTNER, label: "Partner" },
  { value: ModifySessionInitiatedByType.ADMIN, label: "Admin" },
];

//TODO: Will remove this interface after the API is implemented
export interface IEditHistoryTimelineItemChange {
  label: string;
  amount: number;
}

export interface IEditHistoryTimelineItem {
  status: EditTimelineRequestStatus;
  oldTotal: number;
  newTotal: number;
  prepaid: number;
  date: string;
  userRole: string;
  changes: IEditHistoryTimelineItemChange[];
}

export const MOCK_SAMPLE_HISTORY_DATA: IEditHistoryTimelineItem[] = [
  {
    status: EditTimelineRequestStatus.REQUESTED,
    oldTotal: 1348,
    newTotal: 1648,
    prepaid: 1348,
    date: "17 Oct, 4:00 PM",
    userRole: "Admin",
    changes: [{ label: "Full Bath", amount: 500 }, { label: "Teeth Cleaning", amount: -200 }],
  },
  {
    status: EditTimelineRequestStatus.APPROVED,
    oldTotal: 1348,
    newTotal: 1648,
    prepaid: 1348,
    date: "17 Oct, 4:00 PM",
    userRole: "Admin",
    changes: [{ label: "Full Bath", amount: 500 }, { label: "Teeth Cleaning", amount: -200 }],
  },
];